<template>
  <div class="appt-page">
    <Loader v-if="loading" />

    <div v-if="doctor">
      <!-- ── TOP ROW: Profile (left) + Schedule (right) ───── -->
      <div class="row g-3 mb-3">
        <!-- LEFT: Doctor Profile -->
        <div class="col-lg-12">
          <DoctorInfoCard :doctor="doctor" :hospitalName="hospitalName" />
        </div>

        <!-- RIGHT: Date + Slot Picker -->
        <div class="col-lg-12">
          <AppointmentCalendar
            :weekDays="weekDays"
            :selectedDay="selectedDay"
            :selectedSlot="selectedSlot"
            :formattedSelectedDate="formattedSelectedDate"
            :doctor="doctor"
            :errors="errors"
            @change-week="changeWeek"
            @select-date="selectDate"
            @select-slot="selectSlot"
          />
        </div>
      </div>

      <!-- ── BOTTOM: Patient Form ──────────────────────────── -->
      <PatientDetailsForm
        v-if="!showOtpForm && !showSuccessCard"
        :model-value="form"
        @update:model-value="updateForm"
        :errors="errors"
        :genderOptions="genderOptions"
        @submit="submitForm"
      />

      <!-- ── OTP CONFIRMATION ───────────────────── -->
      <OtpVerification
        v-else-if="showOtpForm"
        :mobile="form.mobile"
        :email="form.email"
        v-model:otpDigits="otpDigits"
        :resendTimer="resendTimer"
        :loading="loading"
        :errors="errors"
        @verify="verifyOtp"
        @resend="resendOtp"
        @back="showOtpForm = false"
      />

      <!-- ── SUCCESS CARD ────────────────────────── -->
      <SuccessCard
        v-else-if="showSuccessCard"
        :doctorName="doctor.name"
        :selectedDate="formattedSelectedDate"
        :selectedSlot="selectedSlot"
        @close="onSuccessClose"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import handleOnlineAppointment from '@/services/modules/online-appointment/onlineAppointment';
import Loader from '@/components/atom/LoaderComponent';
import { 
  DAY_NAMES, 
  MONTH_NAMES, 
  formatDateToYYYYMMDD, 
  calculateDobFromAge, 
  calculateAgeFromDob, 
  formatSelectedDate, 
  formatTimeTo24h, 
  getWeekDays,
  createDayObject
} from '@/services/utils/helpers/DateTimeFormatter';

import DoctorInfoCard from '@/components/molecule/online-appointment/DoctorInfoCard.vue';
import AppointmentCalendar from '@/components/molecule/online-appointment/AppointmentCalendar.vue';
import PatientDetailsForm from '@/components/molecule/online-appointment/PatientDetailsForm.vue';
import OtpVerification from '@/components/molecule/online-appointment/OtpVerification.vue';
import SuccessCard from '@/components/molecule/online-appointment/SuccessCard.vue';

const route = useRoute();
const store = useStore();
const showError = inject('showError');
const { fetchSingleResource, fetchDoctorSchedule, verifyAppointment, verifyRegisterPatient, resendOtp: resendOtpApi, fetchGenderList } = handleOnlineAppointment();

const genderOptions = ref([]);

const genderIcons = {
  male: 'fas fa-mars',
  female: 'fas fa-venus',
  other: 'fas fa-genderless',
};

const loading = ref(false);
const doctor = ref(null);
const weekOffset = ref(0);
const selectedDay = ref(null);
const selectedSlot = ref(null);
const isSyncing = ref(false);
const errors = reactive({});
const SLOTS = reactive({
  0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [],
});
const showOtpForm = ref(false);
const showSuccessCard = ref(false);
const otpDigits = ref(['', '', '', '', '', '']);
const resendTimer = ref(0);
const appointmentPayload = ref(null);
const form = reactive({
  name: '',
  mobile: '',
  email: '',
  age_year: '',
  age_month: '',
  age_day: '',
  dob: '',
  gender: '',
  address: '',
  password: '',
  password_confirmation: '',
});

const updateForm = (newData) => {
  Object.assign(form, newData);
};

watch(() => form.name, () => { if (errors.name) delete errors.name; });
watch(() => form.mobile, () => { if (errors.mobile) delete errors.mobile; });
watch(() => form.email, () => { if (errors.email) delete errors.email; });
watch(() => form.gender, () => { if (errors.gender) delete errors.gender; });
watch(() => form.address, () => { if (errors.address) delete errors.address; });
watch(() => form.password, () => { if (errors.password) delete errors.password; });
watch(() => form.password_confirmation, () => { if (errors.password_confirmation) delete errors.password_confirmation; });

watch(() => [form.age_year, form.age_month, form.age_day], ([y, m, d]) => {
  if (isSyncing.value) return;
  isSyncing.value = true;
  form.dob = calculateDobFromAge(y, m, d);
  setTimeout(() => { isSyncing.value = false; }, 50);
});

watch(() => form.dob, (newVal) => {
  if (isSyncing.value || !newVal) return;
  isSyncing.value = true;
  const age = calculateAgeFromDob(newVal);
  form.age_year = age.years;
  form.age_month = age.months;
  form.age_day = age.days;
  setTimeout(() => { isSyncing.value = false; }, 50);
});

const hospitalName = computed(() => store.state.dental.company && store.state.dental.company.name || '');

const weekDays = computed(() => getWeekDays(weekOffset.value, SLOTS));

const formattedSelectedDate = computed(() => {
  if (!selectedDay.value) return '';
  return formatSelectedDate(selectedDay.value.date);
});

const changeWeek = (dir) => {
  weekOffset.value += dir;
  selectedDay.value = null;
  selectedSlot.value = null;
};

const selectDate = async (day) => {
  selectedDay.value = day;
  selectedSlot.value = null;
  delete errors.slot;
  await getDoctorSchedule();
};

const selectSlot = (slot) => {
  selectedSlot.value = slot;
  if (slot) delete errors.slot;
};

const registrationType = computed(() => {
  return store.state.dental.company ? store.state.dental.company.registration_type : 'with_signup';
});

const isSignupRequired = computed(() => {
  return registrationType.value === 'with_signup';
});

const validate = () => {
  Object.keys(errors).forEach(key => delete errors[key]);

  if (!selectedDay.value || !selectedSlot.value) {
    errors.slot = 'Please select an appointment date and time.';
  }
  if (!form.name.trim()) {
    errors.name = 'Full name is required.';
  }
  if (!form.mobile.trim()) {
    errors.mobile = 'Mobile number is required.';
  } else if (!/^01[3-9]\d{8}$/.test(form.mobile.replace(/\s|-/g, ''))) {
    errors.mobile = 'Enter a valid 11-digit Bangladesh mobile number (e.g., 01XXXXXXXXX).';
  }

  if (isSignupRequired.value) {
    if (!form.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!form.password) {
      errors.password = 'Password is required.';
    } else if (form.password.length < 6) {
      errors.password = 'Password must be at least 6 characters.';
    }
    if (!form.password_confirmation) {
      errors.password_confirmation = 'Please confirm your password.';
    } else if (form.password !== form.password_confirmation) {
      errors.password_confirmation = 'Passwords do not match.';
    }
  } else {
    // If not signup required, email is optional but must be valid if provided
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'Please enter a valid email address.';
    }
  }

  if (!form.gender) {
    errors.gender = 'Gender is required.';
  }

  return Object.keys(errors).length === 0;
};

const submitForm = async () => {
  
  if (!validate()) {
    showError('Please fix the errors in the form before proceeding.');
    const firstError = document.querySelector('.err-msg');
    if (!firstError) return;
    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  const d = selectedDay.value.date;
  const formattedDate = formatDateToYYYYMMDD(d);

  let startTime = '';
  let endTime = '';

  const parts = selectedSlot.value ? selectedSlot.value.split(' - ') : [];
  if (parts.length === 2) {
    startTime = `${formattedDate} ${formatTimeTo24h(parts[0])}`;
    endTime = `${formattedDate} ${formatTimeTo24h(parts[1])}`;
  }

  const payload = {
    date: formattedDate,
    start_time: startTime,
    end_time: endTime,
    service_resource_id: route.params.resourceId || '',
    company_id: route.query.companyId || '',
    registration_type: registrationType.value,
    ...form,
  };

  loading.value = true;
  try {
    const res = await verifyAppointment(payload);
    if (!res.status) {
      showError(res.message || 'Verification failed');
      loading.value = false;
      return;
    }
    
    if (isSignupRequired.value) {
      appointmentPayload.value = payload;
      showOtpForm.value = true;
      startResendTimer();
    } else {
      showSuccessCard.value = true;
    }
  } catch (error) {
    showError('Something went wrong during verification');
  } finally {
    loading.value = false;
  }
};

const verifyOtp = async () => {
  const otpCode = otpDigits.value.join('');
  if (otpCode.length < 6) {
    errors.otp = 'Please enter all 6 digits.';
    return;
  }
  delete errors.otp;

  loading.value = true;
  try {
    const res = await verifyRegisterPatient({
      ...appointmentPayload.value,
      otp: otpCode
    });

    if (!res.status) {
      showError(res.message || 'OTP verification failed');
      loading.value = false;
      return;
    }
    showOtpForm.value = false;
    showSuccessCard.value = true;
  } catch (error) {
    showError('Something went wrong during OTP verification');
  } finally {
    loading.value = false;
  }
};

const resendOtp = async () => {
  if (resendTimer.value > 0) return;
  
  loading.value = true;
  try {
    const res = await resendOtpApi(appointmentPayload.value);
    if (!res.status) {
      showError(res.message || 'Failed to resend OTP');
      return;
    }
    
    // Clear previous OTP digits and start timer
    otpDigits.value = ['', '', '', '', '', ''];
    if (errors.otp) delete errors.otp;
    
    startResendTimer();
    const showSuccess = inject('showSuccess');
    if (showSuccess) showSuccess('OTP has been resent successfully');
  } catch (error) {
    showError('Something went wrong while resending OTP');
  } finally {
    loading.value = false;
  }
};

const onSuccessClose = () => {
  showSuccessCard.value = false;
  window.location.href = '/';
};

const startResendTimer = () => {
  resendTimer.value = 60;
  const timer = setInterval(() => {
    resendTimer.value--;
    if (resendTimer.value <= 0) clearInterval(timer);
  }, 1000);
};

const initSelectedDay = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const slots = SLOTS[today.getDay()] || [];
  selectedDay.value = createDayObject(today, slots);

  if (slots.length > 0) {
    selectedSlot.value = slots[0];
  }
};

const getDoctorSchedule = async () => {
  if (!doctor.value || !doctor.value.contact_profile_id) return;
  
  if (!selectedDay.value) {
    initSelectedDay();
  }

  const contactProfileId = doctor.value.contact_profile_id;
  const companyId = route.query.companyId || '';
  
  const d = selectedDay.value.date;
  const scheduledDate = formatDateToYYYYMMDD(d);
  
  try {
    const query = `?contact_profile_id=${contactProfileId}&company_id=${companyId}&scheduled_date=${scheduledDate}`;
    const scheduleRes = await fetchDoctorSchedule(query);
    
    if (!scheduleRes.status || !scheduleRes.data || !scheduleRes.data.data) {
      return;
    }
    const idx = selectedDay.value.date.getDay();
    const daySlots = scheduleRes.data.data.map(shift => shift.title);
    
     SLOTS[idx] = daySlots;
     selectedDay.value.slots = daySlots;
     selectedDay.value.hasSlot = daySlots.length > 0;

     if (daySlots.length > 0) {
       selectedSlot.value = daySlots[0];
     }
  } catch (error) {
    // Error handled silently as per requirements to avoid console.log/error
  }
};

const fetchDoctorProfile = async () => {
  const resourceId = route.params.resourceId;
  if (!resourceId) return;

  loading.value = true;
  try {
    const res = await fetchSingleResource(resourceId);
    if (res.status) {
      doctor.value = res.data;
      await getDoctorSchedule();
    }
  } catch (error) {
    // Error handled silently
  } finally {
    loading.value = false;
  }
};

const fetchGenders = async () => {
  try {
    const res = await fetchGenderList();
    if (!res.status || !Array.isArray(res.data)) return;
    genderOptions.value = res.data.map(g => ({
      value: g.value,
      label: g.name,
      icon: genderIcons[g.value.toLowerCase()] || 'fas fa-genderless'
    }));
  } catch (error) {
    console.log(error);
  }
};

// ── LIFECYCLE ──────────────────────────────────────────
onMounted(async () => {
  await fetchGenders();
  fetchDoctorProfile();
  if (route.query.email) {
    form.email = route.query.email;
  }
});
</script>

<style scoped>
/* ── TOKENS ────────────────────────────────────────────── */
.appt-page {
  --primary:       #7367f0;
  --primary-light: #ede9fd;
  --primary-mid:   #9186f3;
  --accent:        #0bc5a4;
  --text-dark:     #2d2d3a;
  --text-muted:    #6e6b7b;
  --border:        #e4e1f0;
  --radius-lg:     14px;
  --radius-md:     9px;
  --radius-sm:     6px;
  --shadow:        0 3px 16px rgba(115,103,240,.1);

  max-width: 980px;
  margin: 0 auto;
  padding: 20px 12px 40px;
  color: var(--text-dark);
  font-size: .9rem;
}
</style>
